
import { defineComponent, ref, computed, onMounted, watch } from 'vue';

interface Technology {
  name: string;
  startYear: number;
  endYear: number | 'present';
  color: string;
  category: string;
  abbreviation?: string;
  description?: string;
  logo?: string;
  jobExperience?: {
    company: string;
    position: string;
    startDate: string;
    endDate: string;
  };
}

interface ProcessedTechnology extends Technology {
  row: number;
  zIndex: number;
  overlaps: ProcessedTechnology[];
  labelPositions: Array<{x: number; text: string; isEntry: boolean}>;
}

export default defineComponent({
  name: 'LocalTechnologyTimeline',
  props: {
    apiUrl: {
      type: String,
      default: '/data/timeline-data.json'
    },
    branding: {
      type: Boolean,
      default: true
    },
    workExperienceData: {
      type: Object,
      default: null
    }
  },
  setup(props) {
    // Reactive data
    const loading = ref(true);
    const error = ref<string | null>(null);
    const technologies = ref<Technology[]>([]);
    const hoveredTech = ref<ProcessedTechnology | null>(null);

    // Timeline configuration
    const pixelsPerYear = 50;
    const currentYear = new Date().getFullYear();
    const startYear = 2008;
    const endYear = currentYear;
    const timelineWidth = (endYear - startYear + 1) * pixelsPerYear;

    // Methods (defined before computed properties that use them)
    const getYearPosition = (year: number): number => {
      return (year - startYear) * pixelsPerYear;
    };

    const getTechStartPosition = (tech: Technology): number => {
      return (tech.startYear - startYear) * pixelsPerYear;
    };

    const getTechWidth = (tech: Technology): number => {
      const actualEndYear = tech.endYear === 'present' ? currentYear : tech.endYear;
      const duration = actualEndYear - tech.startYear;
      return duration * pixelsPerYear - 4; // -4 for spacing
    };

    // Technology extraction from work experience
    const extractTechnologiesFromWorkExperience = (experiences: any[]): Technology[] => {
      if (!experiences || !experiences.length) return [];

      // Technology name mapping and categorization
      const techMapping: Record<string, {name: string; category: string; color: string; logo?: string}> = {
        // Languages
        'go': {name: 'Go', category: 'language', color: '#00ADD8', logo: '/assets/technology_logos/go.webp'},
        'python': {name: 'Python', category: 'language', color: '#3776AB', logo: '/assets/technology_logos/python.png'},
        'javascript': {name: 'JavaScript', category: 'language', color: '#F7DF1E', logo: '/assets/technology_logos/javascript.png'},
        'typescript': {name: 'TypeScript', category: 'language', color: '#3178C6', logo: '/assets/technology_logos/typescript.webp'},
        'rust': {name: 'Rust', category: 'language', color: '#CE422B'},
        'solidity': {name: 'Solidity', category: 'language', color: '#363636'},
        'c': {name: 'C', category: 'language', color: '#A8B9CC'},
        'cpp': {name: 'C++', category: 'language', color: '#00599C', logo: '/assets/technology_logos/cpp.png'},
        'ruby': {name: 'Ruby', category: 'language', color: '#CC342D', logo: '/assets/technology_logos/ruby.png'},
        'dart': {name: 'Dart', category: 'language', color: '#0175C2', logo: '/assets/technology_logos/dart.png'},

        // Frameworks
        'angularjs': {name: 'AngularJS', category: 'framework', color: '#E23237', logo: '/assets/technology_logos/angular.webp'},
        'angular': {name: 'Angular', category: 'framework', color: '#DD0031', logo: '/assets/technology_logos/angular.webp'},
        'react': {name: 'React', category: 'framework', color: '#61DAFB'},
        'vue': {name: 'Vue.js', category: 'framework', color: '#4FC08D', logo: '/assets/technology_logos/vue.webp'},
        'nextjs': {name: 'Next.js', category: 'framework', color: '#000000'},
        'django': {name: 'Django', category: 'framework', color: '#092E20'},
        'flutter': {name: 'Flutter', category: 'framework', color: '#02569B', logo: '/assets/technology_logos/flutter.png'},
        'phoenix': {name: 'Phoenix', category: 'framework', color: '#FD4F00', logo: '/assets/technology_logos/phoenix.png'},
        'react / vue': {name: 'React / Vue', category: 'framework', color: '#61DAFB', logo: '/assets/technology_logos/react.webp'},
        'cosmos-sdk': {name: 'Cosmos SDK', category: 'blockchain', color: '#2E3148'},
        'cosmwasm': {name: 'CosmWasm', category: 'framework', color: '#5064FB'},
        'electron': {name: 'Electron', category: 'framework', color: '#47848F'},
        'meteorjs': {name: 'MeteorJS', category: 'framework', color: '#DE4F4F'},
        'rubyonrails': {name: 'Ruby on Rails', category: 'framework', color: '#CC0000', logo: '/assets/technology_logos/ruby.png'},
        'ruby on rails': {name: 'Ruby on Rails', category: 'framework', color: '#CC0000', logo: '/assets/technology_logos/ruby.png'},
        'wordpress': {name: 'WordPress', category: 'framework', color: '#21759B'},

        // DevOps & Infrastructure
        'docker': {name: 'Docker', category: 'infrastructure', color: '#2496ED', logo: '/assets/technology_logos/docker.webp'},
        'kubernetes': {name: 'Kubernetes', category: 'infrastructure', color: '#326CE5', logo: '/assets/technology_logos/kubernetes.webp'},
        'terraform': {name: 'Terraform', category: 'infrastructure', color: '#7B42BC', logo: '/assets/technology_logos/terraform.png'},
        'helm': {name: 'Helm', category: 'infrastructure', color: '#0F1689'},
        'ansible': {name: 'Ansible', category: 'infrastructure', color: '#EE0000', logo: '/assets/technology_logos/ansible.png'},
        'gcp': {name: 'GCP', category: 'infrastructure', color: '#4A90E2'},

        // Databases
        'postgresql': {name: 'PostgreSQL', category: 'database', color: '#336791', logo: '/assets/technology_logos/postgres.webp'},
        'redis': {name: 'Redis', category: 'database', color: '#DC382D'},
        'mongodb': {name: 'MongoDB', category: 'database', color: '#10AB4D'},
        'influxdb': {name: 'InfluxDB', category: 'database', color: '#2AADF5'},

        // Runtime & Platforms
        'nodejs': {name: 'Node.js', category: 'framework', color: '#68A063', logo: '/assets/technology_logos/node.webp'},
        'aws': {name: 'AWS', category: 'infrastructure', color: '#FF9900'},
        'webassembly': {name: 'WebAssembly', category: 'interop', color: '#654FF0', logo: '/assets/technology_logos/wasm.svg'},
        'linux': {name: 'Linux', category: 'infrastructure', color: '#FCC624'},
        'windows': {name: 'Windows', category: 'other', color: '#0078D4'},

        // Blockchain
        'ethereum': {name: 'Ethereum', category: 'blockchain', color: '#627EEA'},
        'bitcoin': {name: 'Bitcoin', category: 'blockchain', color: '#F7931A'},
        'tendermint': {name: 'Tendermint', category: 'blockchain', color: '#00D2FF'},
        'celestia': {name: 'Celestia', category: 'blockchain', color: '#7C2DFF'},
        'polygon': {name: 'Polygon', category: 'blockchain', color: '#8247E5'},
        'rollkit': {name: 'Rollkit', category: 'blockchain', color: '#FF6B35'},

        // Protocols & APIs
        'protobuf': {name: 'Protocol Buffers', category: 'protocol', color: '#4285F4', logo: '/assets/technology_logos/protobuf.png'},
        'grpc': {name: 'gRPC', category: 'protocol', color: '#4285F4'},
        'gossipsub': {name: 'GossipSub', category: 'protocol', color: '#FF8C00'},
        'libp2p': {name: 'libp2p', category: 'protocol', color: '#FF6B6B', logo: '/assets/technology_logos/libp2p.png'},
        'graphql': {name: 'GraphQL', category: 'protocol', color: '#E10098', logo: '/assets/technology_logos/graphql.png'},
        'web-apis': {name: 'Web APIs', category: 'other', color: '#4CAF50'},

        // Libraries & State Management
        'redux': {name: 'Redux', category: 'framework', color: '#764ABC'},

        // Observability & Monitoring
        'opentelemetry': {name: 'OpenTelemetry', category: 'infrastructure', color: '#F5A623'},

        // Tools
        'capacitor': {name: 'Capacitor', category: 'framework', color: '#119EFF'},
        'create-react-app': {name: 'Create React App', category: 'framework', color: '#09D3AC'},
        'vnc': {name: 'VNC', category: 'other', color: '#4A90E2'},
        'vpn': {name: 'VPN', category: 'other', color: '#4A90E2'},
        'serialtty': {name: 'SerialTTY', category: 'other', color: '#333333'},
        'disasterrecovery': {name: 'Disaster Recovery', category: 'other', color: '#FF6B6B'},
        'disaster recovery': {name: 'Disaster Recovery', category: 'other', color: '#FF6B6B'},
        'networking': {name: 'Networking', category: 'other', color: '#4ECDC4'},
        'tilt': {name: 'Tilt', category: 'infrastructure', color: '#00D2FF'},
        'ignite': {name: 'Ignite', category: 'framework', color: '#FF6B35'},
        'cli': {name: 'CLI', category: 'other', color: '#333333'},

        // Security & Testing
        'codereview': {name: 'Code Review', category: 'security', color: '#8E44AD'},
        'code review': {name: 'Code Review', category: 'security', color: '#8E44AD'},
        'staticanalysis': {name: 'Static Analysis', category: 'security', color: '#9B59B6'},
        'static analysis': {name: 'Static Analysis', category: 'security', color: '#9B59B6'},
        'fuzztesting': {name: 'Fuzz Testing', category: 'security', color: '#E74C3C'},
        'fuzz testing': {name: 'Fuzz Testing', category: 'security', color: '#E74C3C'},
        'jsfuzz': {name: 'js-fuzz', category: 'security', color: '#F39C12'},
        'js-fuzz': {name: 'js-fuzz', category: 'security', color: '#F39C12'},
        'gofuzz': {name: 'go-fuzz', category: 'security', color: '#E67E22'},
        'go-fuzz': {name: 'go-fuzz', category: 'security', color: '#E67E22'},
        'afl': {name: 'AFL', category: 'security', color: '#D35400'},
        'libfuzzer': {name: 'libFuzzer', category: 'security', color: '#E74C3C'},
        'electronegativity': {name: 'Electronegativity', category: 'security', color: '#8E44AD'},

        // Observability & Monitoring
        'signoz': {name: 'SigNoz', category: 'infrastructure', color: '#FF6B35'},

        // Business & Methodology
        'systemdesign': {name: 'System Design', category: 'other', color: '#3498DB'},
        'system-design': {name: 'System Design', category: 'other', color: '#3498DB'},
        'ecommerce': {name: 'E-commerce', category: 'other', color: '#2ECC71'},
        'e-commerce': {name: 'E-commerce', category: 'other', color: '#2ECC71'},
        'systemadministration': {name: 'System Administration', category: 'infrastructure', color: '#34495E'},
        'system-administration': {name: 'System Administration', category: 'infrastructure', color: '#34495E'}
      };

      const techMap = new Map<string, Technology>();

      experiences.forEach(exp => {
        const startYear = parseInt(exp.startDate.substring(0, 4));
        const endYear = exp.endDate === 'present' ? currentYear : parseInt(exp.endDate.substring(0, 4));

        if (exp.technologies) {
          exp.technologies.forEach((tech: string) => {
            const techKey = tech.toLowerCase().replace(/[^a-z0-9]/g, '');
            const techInfo = techMapping[techKey];

            if (techInfo) {
              const existingTech = techMap.get(techInfo.name);

              if (existingTech) {
                // Extend the range if this experience extends it
                existingTech.startYear = Math.min(existingTech.startYear, startYear);
                // Handle endYear comparison with 'present' support
                const existingEnd = existingTech.endYear === 'present' ? currentYear : existingTech.endYear;
                const newEnd = Math.max(existingEnd, endYear);
                existingTech.endYear = newEnd >= currentYear ? 'present' : newEnd;
                // Keep the most recent job experience info
                if (!existingTech.jobExperience || startYear >= parseInt(existingTech.jobExperience.startDate.substring(0, 4))) {
                  existingTech.jobExperience = {
                    company: exp.company,
                    position: exp.position,
                    startDate: exp.startDate,
                    endDate: exp.endDate
                  };
                }
              } else {
                // Create new technology entry
                techMap.set(techInfo.name, {
                  name: techInfo.name,
                  startYear,
                  endYear: endYear >= currentYear ? 'present' : endYear,
                  color: techInfo.color,
                  category: techInfo.category,
                  logo: techInfo.logo,
                  abbreviation: techInfo.name.length > 8 ? techInfo.name.substring(0, 3) : techInfo.name,
                  description: `Used in professional work from ${startYear} to ${endYear === currentYear ? 'present' : endYear}`,
                  jobExperience: {
                    company: exp.company,
                    position: exp.position,
                    startDate: exp.startDate,
                    endDate: exp.endDate
                  }
                });
              }
            }
          });
        }
      });

      return Array.from(techMap.values());
    };

    // Computed properties
    const showBranding = computed(() => props.branding);

    // Project/position-based row assignment algorithm
    const assignToRows = (techs: Technology[]): ProcessedTechnology[] => {
      if (!techs.length) return [];

      const maxRows = 4;

      // Group technologies by their job experience (company + position + dates)
      const jobGroups = new Map<string, Technology[]>();
      const ungroupedTechs: Technology[] = [];

      techs.forEach(tech => {
        if (tech.jobExperience) {
          const jobKey = `${tech.jobExperience.company}-${tech.jobExperience.position}-${tech.jobExperience.startDate}-${tech.jobExperience.endDate}`;
          if (!jobGroups.has(jobKey)) {
            jobGroups.set(jobKey, []);
          }
          const group = jobGroups.get(jobKey);
          if (group) {
            group.push(tech);
          }
        } else {
          // Technologies without job experience go to ungrouped
          ungroupedTechs.push(tech);
        }
      });

      // Sort job groups by start date (most recent first)
      const sortedJobGroups = Array.from(jobGroups.entries()).sort((a, b) => {
        const aStartYear = parseInt((a[1][0].jobExperience && a[1][0].jobExperience.startDate.substring(0, 4)) || '0');
        const bStartYear = parseInt((b[1][0].jobExperience && b[1][0].jobExperience.startDate.substring(0, 4)) || '0');
        return bStartYear - aStartYear;
      });

      const result: ProcessedTechnology[] = [];
      let currentRow = 0;

      // Assign each job group to consecutive rows
      sortedJobGroups.forEach(([, jobTechs]) => {
        // Sort technologies within each job by start year
        const sortedJobTechs = jobTechs.sort((a, b) => a.startYear - b.startYear);

        sortedJobTechs.forEach(tech => {
          result.push({
            ...tech,
            row: currentRow,
            zIndex: tech.startYear + currentRow * 100,
            overlaps: [],
            labelPositions: []
          });
        });

        // Move to next row for next job group
        currentRow = (currentRow + 1) % maxRows;
      });

      // Handle ungrouped technologies (fallback for sample data)
      if (ungroupedTechs.length > 0) {
        const sortedUngrouped = ungroupedTechs.sort((a, b) => a.startYear - b.startYear);

        sortedUngrouped.forEach(tech => {
          result.push({
            ...tech,
            row: currentRow,
            zIndex: tech.startYear + currentRow * 100,
            overlaps: [],
            labelPositions: []
          });

          // Move to next row for next ungrouped tech
          currentRow = (currentRow + 1) % maxRows;
        });
      }

      return result;
    };

    const processedTechnologies = computed(() => {
      if (!technologies.value.length) return [];

      const assigned = assignToRows(technologies.value);

      // Enhanced label positioning with overlap quality awareness
      assigned.forEach(tech => {
        tech.labelPositions = [];
        tech.overlaps = [];

        // Add entry label at start position with left margin
        tech.labelPositions.push({
          x: getTechStartPosition(tech) + 8, // 8px margin from left edge of bar
          text: tech.abbreviation || tech.name,
          isEntry: true
        });

        // Find overlaps with other techs in same row
        const sameRowTechs = assigned.filter(other =>
          other.row === tech.row && other.name !== tech.name
        );

        const overlapGroups: Array<{
          techs: ProcessedTechnology[];
          start: number;
          end: number;
          duration: number;
          quality: 'large' | 'medium' | 'small';
        }> = [];

        sameRowTechs.forEach(other => {
          // Check if they overlap in time
          const techEnd = tech.endYear === 'present' ? currentYear : tech.endYear;
          const otherEnd = other.endYear === 'present' ? currentYear : other.endYear;
          const overlapStart = Math.max(tech.startYear, other.startYear);
          const overlapEnd = Math.min(techEnd, otherEnd);

          if (overlapStart <= overlapEnd) {
            const duration = overlapEnd - overlapStart + 1;

            // Add to overlaps array
            if (!tech.overlaps.some(o => o.name === other.name)) {
              tech.overlaps.push(other);
            }

            // Determine overlap quality
            let quality: 'large' | 'medium' | 'small';
            if (duration > 2) quality = 'large';
            else if (duration >= 1) quality = 'medium';
            else quality = 'small';

            // Group overlaps for better labeling
            const existingGroup = overlapGroups.find(group =>
              Math.abs(group.start - overlapStart) <= 1 &&
              Math.abs(group.end - overlapEnd) <= 1
            );

            if (existingGroup) {
              existingGroup.techs.push(other);
            } else {
              overlapGroups.push({
                techs: [other],
                start: overlapStart,
                end: overlapEnd,
                duration,
                quality
              });
            }
          }
        });

        // Skip overlap labels - they're redundant after the improved grouping
        // The entry labels at the start of each technology bar are sufficient

        // Sort label positions by x coordinate and remove duplicates
        tech.labelPositions.sort((a, b) => a.x - b.x);

        // Remove duplicate labels that are too close together
        tech.labelPositions = tech.labelPositions.filter((label, index) => {
          if (index === 0) return true;
          const prevLabel = tech.labelPositions[index - 1];
          return Math.abs(label.x - prevLabel.x) > 25; // Minimum 25px separation
        });
      });

      return assigned;
    });

    const yearMarkers = computed(() => {
      const years = [];
      for (let year = startYear; year <= endYear; year++) {
        years.push(year);
      }
      return years;
    });

    const categories = computed(() => {
      const cats = new Set(technologies.value.map(t => t.category));
      return Array.from(cats).sort();
    });

    // Comprehensive tooltip positioning function
    const calculateTooltipPosition = (tech: ProcessedTechnology) => {
      const startPos = getTechStartPosition(tech);
      const width = getTechWidth(tech);
      const barCenterX = startPos + width / 2;
      const barTop = tech.row * 40; // Each row is 40px apart
      const barHeight = 28; // Technology bar height
      const tooltipHeight = 120; // Estimated tooltip height (adjust as needed)
      const tooltipWidth = 200; // min-width from CSS
      const margin = 10; // Margin around tooltip

      // Timeline container dimensions
      const timelineHeight = 140; // From CSS
      const timelineLeftMargin = 20; // From CSS margin
      const timelineWidth = (endYear - startYear + 1) * pixelsPerYear;

      // Vertical positioning logic
      const spaceAbove = barTop;
      const spaceBelow = timelineHeight - (barTop + barHeight);
      const needsSpaceAbove = tooltipHeight + margin;
      const needsSpaceBelow = tooltipHeight + margin;

      let verticalPosition;
      let showBelow = false;

      if (spaceAbove >= needsSpaceAbove && spaceBelow >= needsSpaceBelow) {
        // Both positions work, prefer above for better UX
        verticalPosition = {
          bottom: `${timelineHeight - barTop + margin}px`
        };
      } else if (spaceBelow >= needsSpaceBelow) {
        // Only below works
        verticalPosition = {
          top: `${barTop + barHeight + margin}px`
        };
        showBelow = true;
      } else if (spaceAbove >= needsSpaceAbove) {
        // Only above works
        verticalPosition = {
          bottom: `${timelineHeight - barTop + margin}px`
        };
      } else {
        // Neither position has enough space, use the one with more space
        if (spaceBelow > spaceAbove) {
          verticalPosition = {
            top: `${barTop + barHeight + 5}px` // Reduced margin when space is tight
          };
          showBelow = true;
        } else {
          verticalPosition = {
            bottom: `${timelineHeight - barTop + 5}px` // Reduced margin when space is tight
          };
        }
      }

      // Horizontal positioning logic
      const halfTooltipWidth = tooltipWidth / 2;
      const spaceLeft = barCenterX - timelineLeftMargin;
      const spaceRight = timelineWidth - (barCenterX - timelineLeftMargin);

      let horizontalPosition;

      if (spaceLeft >= halfTooltipWidth && spaceRight >= halfTooltipWidth) {
        // Center alignment works
        horizontalPosition = {
          left: `${barCenterX}px`,
          transform: 'translateX(-50%)'
        };
      } else if (spaceLeft < halfTooltipWidth) {
        // Too close to left edge, align left
        const minLeft = timelineLeftMargin + 5; // 5px minimum margin from edge
        horizontalPosition = {
          left: `${Math.max(minLeft, startPos)}px`,
          transform: 'translateX(0%)'
        };
      } else {
        // Too close to right edge, align right
        const maxRight = timelineLeftMargin + timelineWidth - 5; // 5px minimum margin from edge
        horizontalPosition = {
          left: `${Math.min(maxRight, startPos + width)}px`,
          transform: 'translateX(-100%)'
        };
      }

      return {
        position: { ...verticalPosition, ...horizontalPosition },
        showBelow
      };
    };

    const tooltipStyle = computed(() => {
      if (!hoveredTech.value) return {};

      const { position } = calculateTooltipPosition(hoveredTech.value);
      return position;
    });

    const tooltipShowBelow = computed(() => {
      if (!hoveredTech.value) return false;

      const { showBelow } = calculateTooltipPosition(hoveredTech.value);
      return showBelow;
    });

    const getCategoryColor = (category: string): string => {
      const categoryColors: Record<string, string> = {
        'language': '#3178C6',
        'framework': '#4FC08D',
        'infrastructure': '#FF9900',
        'database': '#336791',
        'security': '#dc2626',
        'blockchain': '#627EEA',
        'protocol': '#4285F4',
        'interop': '#b45309',
        'other': '#666'
      };
      return categoryColors[category] || '#666';
    };

    const fetchData = async () => {
      try {
        loading.value = true;
        error.value = null;

        // Load static API data
        const response = await fetch(props.apiUrl);
        if (!response.ok) {
          throw new Error(`Failed to load timeline data: ${response.status}`);
        }

        const data = await response.json();

        // Process technologies to ensure they have the current year structure
        technologies.value = data.technologies.map((tech: any) => {
          // Handle both old (year) and new (startYear/endYear) formats
          if (tech.year && !tech.startYear) {
            return {
              ...tech,
              startYear: tech.year,
              endYear: 'present'
            };
          }
          // Convert numeric endYear to match current year if it equals or exceeds it
          if (typeof tech.endYear === 'number' && tech.endYear >= currentYear) {
            return {
              ...tech,
              endYear: 'present'
            };
          }
          return tech;
        });

      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Failed to load timeline data';
        console.error('Timeline Error:', err);

        // Generate sample data as fallback
        technologies.value = [
          {
            name: 'JavaScript',
            startYear: 2008,
            endYear: currentYear,
            color: '#F7DF1E',
            category: 'language',
            abbreviation: 'JS',
            description: 'Dynamic programming language for web development'
          },
          {
            name: 'Python',
            startYear: 2010,
            endYear: currentYear,
            color: '#3776AB',
            category: 'language',
            abbreviation: 'Py',
            description: 'High-level programming language'
          },
          {
            name: 'React',
            startYear: 2015,
            endYear: currentYear,
            color: '#61DAFB',
            category: 'framework',
            abbreviation: 'React',
            description: 'JavaScript library for building user interfaces'
          }
        ];
      } finally {
        loading.value = false;
      }
    };

    // Lifecycle
    onMounted(() => {
      fetchData();
    });

    // Watch for URL changes
    watch(() => props.apiUrl, () => {
      fetchData();
    });

    return {
      loading,
      error,
      technologies,
      processedTechnologies,
      hoveredTech,
      showBranding,
      timelineWidth,
      yearMarkers,
      categories,
      tooltipStyle,
      tooltipShowBelow,
      currentYear,
      getYearPosition,
      getTechStartPosition,
      getTechWidth,
      getCategoryColor
    };
  }
});
